/**
 * 皮肤 → CSS。
 *
 * 一套皮肤要落成**三个**选择器块，缺一个都会有一种状态是错的：
 *
 * | 块 | 覆盖的状态 |
 * |---|---|
 * | `<sel>` | 浅色（也是"跟随系统 + 系统是浅色"） |
 * | `@media (prefers-color-scheme: dark) { <sel>:not([data-theme="light"]) }` | 跟随系统 + 系统是深色 |
 * | `<sel>[data-theme="dark"]` | 用户在应用里手动选了深色（必须盖过系统偏好） |
 *
 * ⚠️ 第二、三块的**值必须完全一样**。以前这两块是手抄的两份，37 个值抄两遍——
 * 改一边漏一边而且完全静默，表现是"跟随系统时颜色对，手动切深色就有一处不对"。
 * 现在两块都从同一个 `skin.dark` 生成，从结构上不可能不一致。
 *
 * ⚠️ `:not([data-theme="light"])` 那个否定不能省：省了的话，用户手动选"浅色"之后，
 * 系统一入夜就把他的选择推翻。
 */
import { defaultSkin, PALETTE_VARS, SHAPE_VARS } from './tokens.js';
function paletteDecls(palette, indent) {
    return Object.keys(PALETTE_VARS)
        .map((k) => `${indent}${PALETTE_VARS[k]}: ${palette[k]};`)
        .join('\n');
}
function shapeDecls(shape, indent) {
    const lines = [];
    for (const [group, vars] of Object.entries(SHAPE_VARS)) {
        const source = (group === '' ? shape : shape[group]);
        if (!source)
            continue;
        for (const [key, cssVar] of Object.entries(vars)) {
            const value = source[key];
            if (value === undefined)
                continue;
            lines.push(`${indent}${cssVar}: ${value};`);
        }
    }
    return lines.join('\n');
}
/** 一套皮肤的完整 CSS（三个块）。 */
export function skinToCss(skin, options = {}) {
    const selector = options.selector ?? `:root[data-ps-skin="${skin.name}"]`;
    const indent = options.indent ?? '  ';
    const includeShape = options.includeShape !== false;
    const light = [
        `${selector} {`,
        // 让浏览器知道两套都有：滚动条、表单控件、系统 UI 会跟着走
        `${indent}color-scheme: light dark;`,
        paletteDecls(skin.light, indent),
        includeShape ? shapeDecls(skin.shape, indent) : '',
        '}',
    ]
        .filter(Boolean)
        .join('\n');
    const darkBody = paletteDecls(skin.dark, `${indent}${indent}`);
    const systemDark = [
        '@media (prefers-color-scheme: dark) {',
        // :not([data-theme="light"]) —— 用户手动选了浅色时，系统入夜不能推翻他
        `${indent}${selector}:not([data-theme="light"]) {`,
        darkBody,
        `${indent}}`,
        '}',
    ].join('\n');
    const manualDark = [
        `${selector}[data-theme="dark"] {`,
        `${indent}color-scheme: dark;`,
        paletteDecls(skin.dark, indent),
        '}',
    ].join('\n');
    const manualLight = [`${selector}[data-theme="light"] {`, `${indent}color-scheme: light;`, '}'].join('\n');
    return [light, systemDark, manualDark, manualLight].join('\n\n');
}
/** 一次输出多套皮肤（默认那套挂在 `:root` 上，其余按 `data-ps-skin` 区分）。 */
export function skinsToCss(skins, defaultName) {
    return skins
        .map((skin) => {
        const isDefault = defaultName ? skin.name === defaultName : false;
        // 默认皮肤同时挂到 :root 和自己的属性选择器上：
        // 前者让"没设过 data-ps-skin"的页面也有 token（首屏零配置就能用），
        // 后者让用户从别的皮肤切回默认时也能命中。
        return isDefault
            ? [skinToCss(skin, { selector: ':root' }), skinToCss(skin)].join('\n\n')
            : // 非默认皮肤不重复输出 shape：它们通常只改颜色，重复几十行没有意义。
                // 判据是"它的 shape 是不是就是默认那一份"——`defineSkin` 没收到 shape 补丁时
                // 会原样把 base.shape 传下去，所以引用相等就说明没改过，这比逐字段比对更诚实。
                skinToCss(skin, { includeShape: skin.shape !== defaultSkin.shape });
    })
        .join('\n\n');
}
/** 生成的 CSS 里出现的全部变量名，用于测试与自检 */
export function skinVarNames(includeShape = true) {
    const names = Object.values(PALETTE_VARS).slice();
    if (includeShape) {
        for (const vars of Object.values(SHAPE_VARS))
            names.push(...Object.values(vars));
    }
    return names.sort();
}
//# sourceMappingURL=css.js.map